/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../../locales/en-US/descriptors';
import {
  isObjectOrArrayAndEmpty,
  excludeCurrentMonth,
  getFirstDayofSelectedMonth,
  formattedDate
} from '../../../shared/Utils/Utils';
import useConnectorAndDynamicProps from '../../../shared/useConnectorAndDynamicProps/useConnectorAndDynamicProps';
import ConnectionError from '../../../shared/ErrorComponents/ConnectionError';
import withLoadingStatus from '../../../shared/StatusWrappers/LoadingStatus';
import withErrorStatus from '../../../shared/StatusWrappers/ErrorStatus';
import IUtilityCostComponentProps from './IUtilityCostGraphComponentProps';
import UtilityGraphComponent from '../UtilityGraphComponent/UtilityGraphComponent';
import useMounted from '../../../shared/useMount/useMount';
import {
  EUtilityConfig,
  ISiteCard,
  IUtilityCostData
} from '../../IEnergyManagementProps';
import { ApiContext } from '../../../../context/ApiContext';
import { UtilityType } from '../../../shared/Utils/Constants';
import UtilityLineGraphComponent from '../UtilityLineGraphComponent/UtilityLineGraphComponent';

interface IUtilityCost extends ISiteCard {
  data: IUtilityCostData;
  graphType?: string;
}

const UtilityCostGraphComponentContent = (props: IUtilityCost) => {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const { data, onRefresh, graphType } = props;
  if (isObjectOrArrayAndEmpty(data)) {
    return (
      <div className="errorDisplay">
        <ConnectionError
          title={t(d.CONNECTIONERRORTITLE)}
          message={t(d.CONNECTIONERROR)}
          onRefresh={onRefresh}
        />
      </div>
    );
  }
  if (graphType === 'lineGraph') {
    return <UtilityLineGraphComponent data={data} />;
  } else if (graphType === 'barGraph') {
    return <UtilityLineGraphComponent data={data} />;
  } else {
    return <UtilityGraphComponent data={data} />;
  }
};

const UtilityCostGraphComponentWithLoader = withErrorStatus(
  withLoadingStatus(UtilityCostGraphComponentContent)
);

const connectorConfigName = (utilityGraphType: string | undefined) => {
  switch (utilityGraphType) {
    case UtilityType.Gas:
      return EUtilityConfig.gasUtilitySiteLabel;
    case UtilityType.Electricity:
      return EUtilityConfig.electricityUtilitySiteLabel;
    case UtilityType.Electric:
      return EUtilityConfig.electricityUtilitySiteLabel;
    case UtilityType.ElectricCaps:
      return EUtilityConfig.electricityUtilitySiteLabel;
    case UtilityType.Sewerage:
      return EUtilityConfig.sewerageUtilitySiteLabel;
    case UtilityType.Sewer:
      return EUtilityConfig.sewerageUtilitySiteLabel;
    case UtilityType.Water:
      return EUtilityConfig.waterUtilitySiteLabel;
    default:
      return EUtilityConfig.utilityBill;
  }
};

const UtilityCostGraphComponent = (props: IUtilityCostComponentProps) => {
  const [t] = useTranslation();
  const d = descriptors.energyManagement;
  const { requestContext } = props;

  const startTime = getFirstDayofSelectedMonth(
    requestContext.context.startTime!
  );
  const endTime = excludeCurrentMonth(
    requestContext.context.endTime!,
    requestContext.context.startTime!
  );

  const isMounted = useMounted();

  const endTimeNewFormat = formattedDate(endTime);
  const apiContext = useContext(ApiContext);
  const { utilityGraphType, graphType } = apiContext;

  const connectorConfig = {
    name: connectorConfigName(utilityGraphType),
    options: {
      startDate: startTime,
      endDate: endTimeNewFormat
    }
  };

  const [dataConnector] = useConnectorAndDynamicProps({
    connectorConfig,
    requestContext
  });

  const onRefresh = () => dataConnector?.actions?.getAll();

  useEffect(() => {
    if (isMounted) {
      dataConnector?.actions?.getAll();
    }
  }, [requestContext]);

  const { all, loading, error, status } = dataConnector.data;
  const UtilityCost = (all as unknown) as IUtilityCostData;
  const isDataAvailable = UtilityCost ? true : false;

  const [isLoading, setIsLoading] = useState<boolean>(loading);
  const [, setStatusCode] = useState<number | null>(status);
  const [errorInfo, setErrorInfo] = useState(error);
  const [data, setData] = useState({} as IUtilityCostData);

  useEffect(() => {
    setIsLoading(loading);
    if ((isDataAvailable || error || status) && !loading) {
      if (isDataAvailable) {
        setData(UtilityCost);
      }
      setErrorInfo(error);
      setStatusCode(status);
    }
  }, [isDataAvailable, loading]);
  return (
    <UtilityCostGraphComponentWithLoader
      loading={isLoading}
      error={errorInfo}
      statusCode={status}
      onRefresh={onRefresh}
      data={data}
      loadingText={t(d.LOADING)}
      title={t(d.CONNECTIONERRORTITLE)}
      noDataMessage={t(d.NODATAAVAILABLE)}
      connectionErrorMessage={t(d.CONNECTIONERROR)}
      graphType={graphType}
    />
  );
};
export default UtilityCostGraphComponent;
